import React from 'react';
import { Row, Col, Card, Statistic, Empty } from 'antd';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { UserOutlined, ShopOutlined } from '@ant-design/icons';
import type { UserIndicatorsResponse } from '../../services/admin-users.service';

// Phase 5: US3 - Indicators Dashboard (T028, T029, T030)

export interface UserIndicatorsDashboardProps {
  data?: UserIndicatorsResponse;
  loading?: boolean;
}

// Color palette for pie charts
const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

const ECONOMIC_LEVEL_LABELS: Record<string, string> = {
  NO_INCOME: 'Sem renda',
  UP_TO_HALF_MW: 'Até 1/2 SM',
  ONE_TO_TWO_MW: '1 a 2 SM',
  TWO_TO_FIVE_MW: '2 a 5 SM',
  FIVE_TO_TEN_MW: '5 a 10 SM',
  TEN_TO_TWENTY_MW: '10 a 20 SM',
  ABOVE_TWENTY_MW: 'Acima de 20 SM',
};

// T035: Custom pie chart tooltip showing label + absolute count + percentage
const PieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length > 0) {
    const entry = payload[0];
    const total = entry.payload.total as number;
    const count = entry.value as number;
    const percent = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
    const label = entry.name ?? 'Não informado';
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          padding: '8px 12px',
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        <div>{count} usuário{count !== 1 ? 's' : ''}</div>
        <div style={{ color: '#888' }}>{percent}% do total</div>
      </div>
    );
  }
  return null;
};

// Helper: add total to each datum so tooltip can compute percent
function withTotal<T extends { count: number }>(items: T[]) {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  return items.map((item) => ({ ...item, total }));
}

export const UserIndicatorsDashboard: React.FC<UserIndicatorsDashboardProps> = ({ data, loading }) => {
  if (loading) {
    return <div>Carregando indicadores...</div>;
  }

  if (!data) {
    return <Empty description="Sem dados disponíveis" />;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* T028: Statistic Cards for Totals */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Usuários Participantes"
              value={data.totals.participants}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Usuários Contratantes"
              value={data.totals.contractors}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total de Usuários"
              value={data.totals.participants + data.totals.contractors + data.totals.administrators}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* T029: Bar Charts for Registrations */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Cadastros - Últimos 30 Dias" style={{ minHeight: 400 }}>
            {data.registrationsByDay.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.registrationsByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Cadastros - Últimos 12 Meses" style={{ minHeight: 400 }}>
            {data.registrationsByMonth.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.registrationsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#52c41a" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      {/* T030: Pie Charts for Demographics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Distribuição por Nível de Validação">
            {data.byValidationLevel.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={withTotal(data.byValidationLevel)}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, count, total }) => {
                      const percent = ((count / total) * 100).toFixed(1);
                      return `${label ?? 'Não informado'} (${percent}%)`;
                    }}
                  >
                    {data.byValidationLevel.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Distribuição por Gênero">
            {data.byGender.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={withTotal(data.byGender.map((item) => ({
                      ...item,
                      label:
                        item.label === 'MALE'
                          ? 'Masculino'
                          : item.label === 'FEMALE'
                            ? 'Feminino'
                            : item.label,
                    })))}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, count, total }) => {
                      const percent = ((count / total) * 100).toFixed(1);
                      return `${label ?? 'Não informado'} (${percent}%)`;
                    }}
                  >
                    {data.byGender.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Distribuição por Escolaridade">
            {data.byEducationLevel.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={withTotal(data.byEducationLevel)}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, count, total }) => {
                      const percent = ((count / total) * 100).toFixed(1);
                      return `${label ?? 'Não informado'} (${percent}%)`;
                    }}
                  >
                    {data.byEducationLevel.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Distribuição por Nível Econômico">
            {data.byEconomicLevel.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={withTotal(
                      data.byEconomicLevel.map((item) => ({
                        ...item,
                        label: item.label ? ECONOMIC_LEVEL_LABELS[item.label] ?? item.label : item.label,
                      })),
                    )}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, count, total }) => {
                      const percent = ((count / total) * 100).toFixed(1);
                      return `${label ?? 'Não informado'} (${percent}%)`;
                    }}
                  >
                    {data.byEconomicLevel.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Distribuição por Faixa Etária">
            {data.byAgeRange.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={withTotal(data.byAgeRange)}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, count, total }) => {
                      const percent = ((count / total) * 100).toFixed(1);
                      return `${label ?? 'Não informado'} (${percent}%)`;
                    }}
                  >
                    {data.byAgeRange.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Distribuição por Estado">
            {data.byState.length === 0 ? (
              <Empty description="Sem dados disponíveis" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={withTotal(data.byState)}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, count, total }) => {
                      const percent = ((count / total) * 100).toFixed(1);
                      return `${label ?? 'Não informado'} (${percent}%)`;
                    }}
                  >
                    {data.byState.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserIndicatorsDashboard;
